import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Modal,
  Alert,
} from '@mui/material';
import { set } from 'date-fns';

interface Dependente {
  nomeDependente: string;
  grauParentescoNome: string;
  codigoGrauParentesco: number;
  codigoSituacao: number;
}

interface Contato {
  tipo: string;
  descricaoContato: string;
}

interface Associado {
  nome: string;
  cpf: string;
  dataDeNascimento: string;
  dependentes: Dependente[];
  contatos: Contato[];
  codigo: number;
  nomeFantasiaDaEmpresa: string;
}

/* interface ApiResponse {
  dados: Associado[];
} */

const GDFSearch: React.FC = () => {
  const [cpf, setCpf] = useState<string>('');
  const [login, setLogin] = useState<string>('');
  const [senha, setSenha] = useState<string>('');
  const [logado, setLogado] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [celular, setCelular] = useState<string>('');
  const [associado, setAssociado] = useState<Associado | null>(null);
  const [error, setError] = useState<string>('');
  const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
  const [apiLink, setApiLink] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const [loadingLink, setLoadingLink] = useState<boolean>(false);

  const handleCpfChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCpf(event.target.value);
    setError('');
  };
  const handleLoginChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setLogin(event.target.value);
    setError('');
  };
  const handleSenhaChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSenha(event.target.value);
    setError('');
  };
  const handleCelularChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCelular(event.target.value);
    setError('');
  };

  const handleFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();

    const cleanedCpf = cpf.replace(/[^\d]+/g, '');

    if (cleanedCpf.length !== 11) {
      setError('CPF deve conter 11 dígitos');
      return;
    }

    setLoading(true);
    setAssociado(null);
    setSuccessMessage('');

    try {
      const urlBase = process.env.REACT_APP_URL_BASE;
      const token = process.env.REACT_APP_TOKEN_S4E_V2;
      const url = `${urlBase}/v2/api/Associados?token=${token}&cpfAssociado=${cleanedCpf}`;

      const response = await axios.get(url);

      if (response.data.dados.length === 0) {
        setError('Nenhum associado encontrado com este CPF');
        return;
      }

      // Validação 1: Pertence à EMPRESARIAL GDFC
      const associadoData = response.data.dados.filter(
        x => x.codigoDaEmpresa === 27855,
      );
      if (!associadoData) {
        setError('Este CPF não pertence à EMPRESARIAL GDFC');
        return;
      }

      // Validação 2: Associado ativo (titular com situação ativa)
      const associadoAtivo = associadoData.find(
        (x: Associado) =>
          Array.isArray(x.dependentes) &&
          x.dependentes.some(
            (dep: Dependente) =>
              dep.codigoGrauParentesco === 1 &&
              (dep.codigoSituacao === 1 ||
                dep.codigoSituacao === 16 ||
                dep.codigoSituacao === 111 ||
                dep.codigoSituacao === 118),
          ),
      );

      if (!associadoAtivo) {
        setError('Associado não está ativo no sistema');
        return;
      }
      setError('');
      setAssociado(associadoAtivo);
      setSuccessMessage('Associado válido e ativo encontrado!');
    } catch (err) {
      setError('Erro ao buscar associado. Tente novamente mais tarde.');
      console.error('Erro na busca:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    const urlBase = process.env.REACT_APP_URL_BASE;
    const Token = process.env.REACT_APP_TOKEN_S4E_V2;
    const cpfCleaned = login.replace(/[^\d]+/g, '');
    const response = await axios.post(
      `${urlBase}/SYS/services/LoginService.aspx/Colaborador`,
      {
        token: Token,
        cpf: cpfCleaned,
        senha: senha,
      },
    );
    if (response.data.codigo === 1) {
      setLogado(true);
    } else {
      alert('Erro ao fazer Login');
    }
  };

  const handleGenerateLink = async () => {
    if (!associado) return;

    setLoadingLink(true);
    setError('');

    try {
      const UrlApiV2 = process.env.REACT_APP_OG_API_URL;

      const response = await axios.get(
        `${UrlApiV2}/generate/AssinaturaGdf?id_associado=${associado.codigo}`,
      );

      if (response.data && response.data.linkGerado) {
        setApiLink(response.data.linkGerado);
        setCelular(formatPhoneNumber(associado.contatos));
        setShowLinkModal(true);
      } else {
        throw new Error('Resposta da API não contém um link válido');
      }
    } catch (error) {
      console.error('Erro ao gerar link:', error);
      setError('Erro ao gerar link de assinatura. Tente novamente.');
    } finally {
      setLoadingLink(false);
    }
  };

  const formatPhoneNumber = (contatos: Contato[]): string => {
    const phone = contatos.find(c => c.tipo === 'Celular');
    return phone?.descricaoContato || 'Não informado';
  };

  return (
    <Container>
      {!logado ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Paper
            elevation={3}
            style={{ padding: '2rem', maxWidth: '600px', width: '100%' }}
          >
            <Typography variant="h5" component="h3" gutterBottom>
              Login S4e
            </Typography>
            <Typography variant="body1" gutterBottom>
              Faça o Login para gerar o link de Assinatura
            </Typography>

            <form>
              <TextField
                fullWidth
                margin="normal"
                label="Login"
                variant="outlined"
                value={login}
                onChange={handleLoginChange}
                error={!!error}
                helperText={error}
                required
              />
              <TextField
                fullWidth
                margin="normal"
                label="Senha"
                variant="outlined"
                type="password"
                value={senha}
                onChange={handleSenhaChange}
                error={!!error}
                helperText={error}
                required
              />
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={handleLogin}
                >
                  {loading ? <CircularProgress size={24} /> : 'Buscar'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Paper
            elevation={3}
            style={{ padding: '2rem', maxWidth: '600px', width: '100%' }}
          >
            <Typography variant="h5" component="h3" gutterBottom>
              Gerar Proposta
            </Typography>
            <Typography variant="body1" gutterBottom>
              Informe o CPF do servidor GDF
            </Typography>

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            <form onSubmit={handleFormSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label="Número do CPF"
                variant="outlined"
                value={cpf}
                onChange={handleCpfChange}
                error={!!error}
                helperText={error}
                required
              />
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Buscar'}
                </Button>
              </Box>
            </form>

            {associado && (
              <Box mt={4}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" gutterBottom>
                    Dados do Associado
                  </Typography>
                  <Chip
                    label={`Código: ${associado.codigo}`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <List>
                  <ListItem>
                    <ListItemText primary="Nome" secondary={associado.nome} />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText primary="CPF" secondary={associado.cpf} />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Data de Nascimento"
                      secondary={new Date(
                        associado.dataDeNascimento,
                      ).toLocaleDateString()}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Telefone"
                      secondary={formatPhoneNumber(associado.contatos)}
                    />
                  </ListItem>
                  <Divider />
                  {associado.dependentes.length > 0 && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Dependentes"
                          secondary={associado.dependentes
                            .filter(d => d.grauParentescoNome !== 'TITULAR')
                            .map(d => d.nomeDependente)
                            .join(', ')}
                        />
                      </ListItem>
                      <Divider />
                    </>
                  )}
                </List>

                <Box mt={3} display="flex" justifyContent="space-between">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setAssociado(null)}
                  >
                    Nova Consulta
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateLink}
                  >
                    Enviar
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Modal para exibir o link da API */}
      <Modal
        open={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        aria-labelledby="link-modal-title"
        aria-describedby="link-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
          }}
        >
          <Typography id="link-modal-title" variant="h6" component="h2">
            Link Gerado
          </Typography>
          <Typography id="link-modal-description" sx={{ mt: 2 }}>
            Link para assinatura da proposta:
          </Typography>
          <TextField
            fullWidth
            value={apiLink}
            margin="normal"
            InputProps={{
              readOnly: true,
            }}
          />

          {/* Nova seção para envio por WhatsApp */}
          {associado && (
            <>
              <Typography sx={{ mt: 3 }}>Enviar link por WhatsApp</Typography>
              <TextField
                fullWidth
                value={celular}
                margin="normal"
                label="Número de telefone"
                onChange={handleCelularChange}
              />
              <Box
                sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    try {
                      const urlBase = process.env.REACT_APP_OG_API_URL;
                      const response = await axios.post(
                        `${urlBase}/register/SendWhatsApp`,
                        {
                          number: Number(celular),
                          name: associado.nome,
                          id: apiLink.replace('https://assina.ae/', ''),
                          vendedor: 65083,
                        },
                      );

                      // Exemplo de tratamento de resposta
                      if (response.status === 200) {
                        alert('Mensagem enviada com sucesso!');
                        setAssociado(null);
                        setShowLinkModal(false);
                      } else {
                        alert('Erro ao enviar mensagem. Tente novamente.');
                      }
                    } catch (error) {
                      console.error('Erro ao enviar mensagem:', error);
                      alert('Erro ao enviar mensagem. Tente novamente.');
                    }
                  }}
                >
                  Enviar
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowLinkModal(false)}>Fechar</Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default GDFSearch;
